import React from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Dialog } from "../../../../../../components/Dialogs/Dialog";
import { api } from "../../../../../../api/api";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../../../../hooks";
import { showMessage } from "../../../../../../redux/messages/messagesActions";
import { formatText } from "../../../../../../helpers";

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface IAutomobilePolicyForm {
  incremental_number: number;
  task_code: string;
  policy_number: string;
  customer_name: string;
  customer_code: string;
  policy_form_type: string;
  create_ts: string | null;
  created_by_name: number;
  status: string;
}

interface IGenerateDocumentPayload {
  customer_id: number;
  customer_policy_id: number;
  application_number: number;
  application_type: string;
  policy_forms_numbers: Array<{
    increment_number: number;
    type: string;
  }>;
}

export interface IApplicationG4SignDialogProps {
  open: boolean;
  applicationNumber: number;
  applicationType: string;
  customerPolicyId: number;
  customerId: number;
  onClose: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ApplicationG4SignDialog: React.FC<IApplicationG4SignDialogProps> = ({
  open,
  applicationNumber,
  applicationType,
  customerPolicyId,
  customerId,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();

  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [policyForms, setPolicyForms] = React.useState<IAutomobilePolicyForm[]>(
    []
  );
  const [selectedFormKeys, setSelectedFormKeys] = React.useState<
    Set<string>
  >(new Set());

  // Helper to create a unique key for each form
  const getFormKey = (form: IAutomobilePolicyForm) =>
    `${form.incremental_number}::${form.policy_form_type}`;

  // Fetch policy forms when dialog opens
  React.useEffect(() => {
    if (open) {
      fetchPolicyForms();
    }
    return () => {
      // Reset state on close
      setPolicyForms([]);
      setSelectedFormKeys(new Set());
      setError(null);
    };
  }, [open]);

  const fetchPolicyForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `/policy/get-policy-commercial-and-automobile-all-policy-forms?customer_policy_id=${customerPolicyId}&policy_form_type=all_forms&table_name=automobile&application_number=${applicationNumber}&pageNo=1&itemPerPage=50`;
      const response = await api.get(url);
      const data: IAutomobilePolicyForm[] = response.data.data || [];
      setPolicyForms(data);
      // Select all policy forms by default
      setSelectedFormKeys(new Set(data.map((f) => getFormKey(f))));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to fetch policy forms. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleForm = (form: IAutomobilePolicyForm) => {
    const key = getFormKey(form);
    setSelectedFormKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedFormKeys.size === policyForms.length) {
      setSelectedFormKeys(new Set());
    } else {
      setSelectedFormKeys(
        new Set(policyForms.map((f) => getFormKey(f)))
      );
    }
  };

  const handleNext = async () => {
    setSubmitting(true);
    try {
      const selectedForms = policyForms.filter((f) =>
        selectedFormKeys.has(getFormKey(f))
      );

      const payload: IGenerateDocumentPayload = {
        customer_id: customerId,
        customer_policy_id: customerPolicyId,
        application_number: applicationNumber,
        application_type: applicationType,
        policy_forms_numbers: selectedForms.map((f) => ({
          increment_number: f.incremental_number,
          type: f.policy_form_type,
        })),
      };

      const response = await api.post(
        "/docusign/generate-document-for-signing",
        payload
      );
      const documentCode = response.data.data.document_code;

      if (!documentCode) {
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to generate document. No document code returned.",
            displayAs: "snackbar",
          })
        );
        return;
      }

      onClose();
      navigate(
        `/automobile-policy/${customerId}/g4-sign/${customerPolicyId}/manage/${documentCode}`
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ||
            "Failed to generate document for signing.",
          displayAs: "snackbar",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isNextDisabled = selectedFormKeys.size === 0 || submitting;

  return (
    <Dialog
      open={open}
      title="G4Sign - Generate Document for Signing"
      size="md"
      fullWidth
      onClose={onClose}
      actions={[
        {
          type: "button",
          variant: "outlined",
          label: "Close",
          onClick: onClose,
        },
        {
          type: "button",
          variant: "contained",
          label: submitting ? "Generating..." : "Next",
          disabled: isNextDisabled,
          onClick: handleNext,
        },
      ]}
    >
      {/* Section 1: Application Info Boxes */}
      <Stack direction="row" spacing={2} mb={3}>
        <Box
          sx={{
            flex: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="body2" color="textSecondary" mb={0.5}>
            Application Number
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {applicationNumber}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="body2" color="textSecondary" mb={0.5}>
            Application Type
          </Typography>
          <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
            {formatText(applicationType || "")}
          </Typography>
        </Box>
      </Stack>

      {/* Section 2: Policy Forms Table */}
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Policy Forms
      </Typography>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography color="error">{error}</Typography>
        </Box>
      ) : policyForms.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No policy forms available for this application.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedFormKeys.size > 0 &&
                      selectedFormKeys.size < policyForms.length
                    }
                    checked={selectedFormKeys.size === policyForms.length}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>Form Type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policyForms.map((form) => (
                <TableRow key={getFormKey(form)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedFormKeys.has(
                        getFormKey(form)
                      )}
                      onChange={() =>
                        handleToggleForm(form)
                      }
                    />
                  </TableCell>
                  <TableCell>{form.incremental_number}</TableCell>
                  <TableCell>{formatText(form.policy_form_type || "")}</TableCell>
                  <TableCell>{form.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Dialog>
  );
};
